import { parse, parseIcu, type MessageNode } from 'verbaly';

// trees of the last gate pass, read again by the next rebuild: core's cache caps below a big app
let remembered = new Map<string, MessageNode[]>();
let reading: Map<string, MessageNode[]> | undefined;

// the gate reads every catalog on disk, so it always has the parser the runtime only opts into
function read(message: string): MessageNode[] {
  if (!reading) return parse(message, parseIcu);
  let tree = reading.get(message);
  if (!tree) {
    tree = remembered.get(message) ?? parse(message, parseIcu);
    reading.set(message, tree);
  }
  return tree;
}

// only check() remembers, so memory follows the catalogs: a save outside it keeps nothing
export function gatePass<T>(run: () => T): T {
  reading = new Map();
  try {
    return run();
  } finally {
    remembered = reading;
    reading = undefined;
  }
}

export const PLURAL_CATEGORIES = new Set(['zero', 'one', 'two', 'few', 'many']);
export type IssueSeverity = 'error' | 'warning';

export interface StructureIssue {
  severity: IssueSeverity;
  message: string;
}

interface VariantShape {
  selectors: string[];
  plural: boolean;
  ordinal: boolean;
}

const EXACT = /^=\d+$/;

// param name → its plural/select block, when it has one; first block per name wins
function shapes(message: string): Map<string, VariantShape> {
  const out = new Map<string, VariantShape>();
  walk(read(message), out);
  return out;
}

function walk(nodes: MessageNode[], out: Map<string, VariantShape>): void {
  for (const node of nodes) {
    if (node.kind !== 'param' || !node.variants) continue;
    const selectors = node.variants.map(([key]) => key);
    if (!out.has(node.name)) {
      out.set(node.name, {
        selectors,
        plural: selectors.some((key) => PLURAL_CATEGORIES.has(key)),
        ordinal: node.ordinal === true,
      });
    }
    for (const [, body] of node.variants) walk(body, out);
  }
}

function paramNames(message: string): string[] {
  const names = new Set<string>();
  collect(read(message), names);
  return [...names].sort();
}

function collect(nodes: MessageNode[], out: Set<string>): void {
  for (const node of nodes) {
    if (node.kind !== 'param') continue;
    out.add(node.name);
    if (node.variants) for (const [, body] of node.variants) collect(body, out);
  }
}

const TAG = /<(\/?)([a-zA-Z][\w-]*)\s*(\/?)>/g;
// the breaks core's parseTags reads whole however they are written: keep the two lists equal
const BREAK = /^(?:br|wbr)$/;

// markup is a closed (</x>), self-closed (<x/>) or break tag (<br>): a lone <Enter> is prose
function markupNames(message: string): Set<string> {
  const out = new Set<string>();
  for (const match of message.matchAll(TAG)) {
    if (match[1] || match[3] || BREAK.test(match[2]!)) out.add(match[2]!);
  }
  return out;
}

// read as the runtime reads it: <br>, <br/> and <br /> are one break, and </br> is no tag at all
function tagTokens(message: string, markup: Set<string>): string[] {
  const out: string[] = [];
  for (const [, closing, name, selfClose] of message.matchAll(TAG)) {
    if (!markup.has(name!)) continue;
    if (!BREAK.test(name!)) out.push(`${closing}${name}${selfClose}`);
    else if (!closing) out.push(`${name}/`);
  }
  return out.sort();
}

// items in a that are missing from b, counting duplicates
function surplus(a: string[], b: string[]): string[] {
  const rest = [...b];
  const out: string[] = [];
  for (const item of a) {
    const at = rest.indexOf(item);
    if (at < 0) out.push(item);
    else rest.splice(at, 1);
  }
  return out;
}

function list(items: string[]): string {
  return items.length > 1
    ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    : items[0]!;
}

// a tagged template escapes a block around a bare name, so it ships as literal text
const ESCAPED_SYNTAX = /(?<!\{)\{\{\s*([\w.]+)(\s*[|:][^{}]*)\}\}/;

// ICU in a tagged template is escaped the same way; its branches nest, so only its head shows
const ESCAPED_ICU = /(?<!\{)\{\{\s*([\w.]+)\s*,\s*(plural|selectordinal|select|number|date|time)\b/;
const ICU_SLICE = /^\{\{([\w.]+), (\w+), …\}\}$/;

// a block written around ${…} that never closes: the whole template is escaped, its head included
const ESCAPED_OPEN = /(?<!\{)\{\{\{([\w.]+)\}(\s*\|\s*[^\s|{}:]+\s*:|:[A-Za-z_][\w-]*)/;

// the escaped slice of an extracted message, when the author meant a plural or format block
export function escapedSyntax(message: string): string | undefined {
  const icu = ESCAPED_ICU.exec(message);
  if (icu) return `{{${icu[1]}, ${icu[2]}, …}}`;
  const open = ESCAPED_OPEN.exec(message);
  if (open) return `{\${${open[1]}}${open[2]}…`;
  return ESCAPED_SYNTAX.exec(message)?.[0];
}

// the slice as the author wrote it in the template: escaped pairs read as one brace
export function shownSlice(slice: string): string {
  return slice.startsWith('{{') ? slice.slice(1, -1) : slice;
}

// the same block as a tagged template reads it: the value in ${…}, a format right after the colon
export function inlineBlock(slice: string): string {
  if (slice.startsWith('{${')) return 'the same block with its closing }';
  const icu = ICU_SLICE.exec(slice);
  if (icu) {
    const [, name = '', type = ''] = icu;
    if (type === 'plural') return `{\${${name}} | one: … | other: …}`;
    if (type === 'select') return `{\${${name}} | …: … | other: …}`;
    // the catalog syntax has no ordinal rules: only ICU, and only from a catalog, can say 1st
    if (type === 'selectordinal') return 't(key, params), with the ICU message in the catalog';
    return `{\${${name}}:${type}}`;
  }
  const match = ESCAPED_SYNTAX.exec(slice);
  if (!match) return slice;
  const [, name = '', tail = ''] = match;
  const rest = tail.trimStart().startsWith(':')
    ? `:${tail.trimStart().slice(1).trimStart()}`
    : tail;
  return `{\${${name}}${rest}}`;
}

// one message on its own: defects that need no source to be visible
export function validateMessage(message: string, locale?: string): StructureIssue[] {
  const issues: StructureIssue[] = [];
  for (const [name, shape] of shapes(message)) {
    if (!shape.plural) continue; // a select block without 'other' is the author's design
    if (!shape.selectors.includes('other')) {
      issues.push({
        severity: 'error',
        message: `the plural block for {${name}} has no "other" case, so any count it does not list renders empty`,
      });
      continue;
    }
    if (!locale) continue;
    const missing = reachableCategories(locale, shape.ordinal).filter(
      (category) => category !== 'other' && !shape.selectors.includes(category),
    );
    if (missing.length > 0) {
      issues.push({
        severity: 'warning',
        message: `${locale} also needs the ${list(missing)} plural ${missing.length > 1 ? 'forms' : 'form'} for {${name}}`,
      });
    }
  }
  return issues;
}

// a translation against the source it must mirror
export function validatePair(source: string, translated: string): StructureIssue[] {
  const issues: StructureIssue[] = [];

  const lostParams = surplus(paramNames(source), paramNames(translated));
  const newParams = surplus(paramNames(translated), paramNames(source));
  if (lostParams.length > 0) {
    issues.push({
      severity: 'error',
      message: `${list(lostParams.map((name) => `{${name}}`))} ${lostParams.length > 1 ? 'are' : 'is'} missing, so ${lostParams.length > 1 ? 'their values never reach' : 'its value never reaches'} the text`,
    });
  }
  if (newParams.length > 0) {
    issues.push({
      severity: 'error',
      message: `${list(newParams.map((name) => `{${name}}`))} ${newParams.length > 1 ? 'are' : 'is'} not in the source message, so ${newParams.length > 1 ? 'they render' : 'it renders'} as literal text`,
    });
  }

  // one vocabulary for both sides: a tag the translation invents is still caught
  const markup = new Set([...markupNames(source), ...markupNames(translated)]);
  const lostTags = tagReport(surplus(tagTokens(source, markup), tagTokens(translated, markup)));
  const newTags = tagReport(surplus(tagTokens(translated, markup), tagTokens(source, markup)));
  if (lostTags.length > 0) {
    issues.push({
      severity: 'error',
      message: `${list(lostTags)} ${lostTags.length > 1 ? 'are' : 'is'} missing from the translation`,
    });
  }
  if (newTags.length > 0) {
    issues.push({
      severity: 'error',
      message: `${list(newTags)} ${newTags.length > 1 ? 'are' : 'is'} not in the source message`,
    });
  }

  const target = shapes(translated);
  for (const [name, shape] of shapes(source)) {
    const other = target.get(name);
    if (!other) {
      issues.push({
        severity: 'error',
        message: `the ${shape.plural ? 'plural' : 'select'} block for {${name}} was flattened into plain text`,
      });
      continue;
    }
    // 'other' is the catch-all: losing it renders empty (the plural case is validateMessage's)
    if (shape.selectors.includes('other') && !other.selectors.includes('other') && !other.plural) {
      issues.push({
        severity: 'error',
        message: `the "other" case for {${name}} is missing, so unlisted values render empty`,
      });
    }
    const lostExact = shape.selectors
      .filter((key) => EXACT.test(key))
      .filter((key) => !other.selectors.includes(key));
    if (lostExact.length > 0) {
      issues.push({
        severity: 'warning',
        message: `the ${list(lostExact)} ${lostExact.length > 1 ? 'cases' : 'case'} for {${name}} ${lostExact.length > 1 ? 'are' : 'is'} missing, so ${lostExact.length > 1 ? 'those counts' : 'that count'} falls back to "other"`,
      });
    }
  }

  return issues;
}

// one entry per tag name: a translator restores the pair, not a lone closing tag
function tagReport(tokens: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const token of tokens) {
    const name = token.replaceAll('/', '');
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(token.endsWith('/') ? `<${name}/>` : `<${name}>`);
  }
  return out;
}

const COUNT_PROBE = 200; // the range a UI counter realistically reaches
const reachable = new Map<string, string[]>();

function reachableCategories(locale: string, ordinal: boolean): string[] {
  const type = ordinal ? 'ordinal' : 'cardinal';
  // the escape, never the raw character: a literal NUL makes git treat the file as binary
  const cacheKey = `${locale}\u0000${type}`;
  const hit = reachable.get(cacheKey);
  if (hit) return hit;

  let out: string[];
  try {
    // pt_BR formats as pt-BR at runtime, so its plurals are pt-BR's here too
    const rules = new Intl.PluralRules(locale.replace(/_/g, '-'), { type });
    const seen = new Set<string>();
    for (let n = 0; n <= COUNT_PROBE; n++) seen.add(rules.select(n));
    out = [...seen];
  } catch {
    out = []; // unknown tag: nothing is ever reported missing
  }
  reachable.set(cacheKey, out);
  return out;
}
